import React, { Suspense } from 'react';
import { Loader2 } from 'lucide-react';

interface LazyPageWrapperProps {
  children: React.ReactNode;
  fallbackText?: string;
}

const PageLoader = ({ text = "Loading..." }: { text?: string }) => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center space-y-4">
      <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
      <p className="text-muted-foreground animate-pulse">{text}</p>
    </div>
  </div>
);

const ErrorFallback = ({ error }: { error: Error }) => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4">
    <div className="text-center space-y-4 max-w-md">
      <h2 className="text-xl font-semibold text-destructive">Something went wrong</h2>
      <p className="text-muted-foreground">{error.message}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Reload Page
      </button>
    </div>
  </div>
);

export const LazyPageWrapper: React.FC<LazyPageWrapperProps> = ({ 
  children, 
  fallbackText 
}) => {
  return (
    <Suspense fallback={<PageLoader text={fallbackText} />}>
      {children}
    </Suspense>
  );
};

// Enhanced lazy component creator with retry mechanism
export function createLazyComponent<T extends React.ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  displayName?: string,
  fallbackText?: string
) {
  const LazyComponent = React.lazy(async () => {
    try {
      const module = await importFunc();
      return module;
    } catch (error) {
      console.error(`Failed to load ${displayName || 'component'}:`, error);
      // Retry once after a short delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      return importFunc();
    }
  });

  return React.memo((props: React.ComponentProps<T>) => (
    <LazyPageWrapper fallbackText={fallbackText || `Loading ${displayName || 'page'}...`}>
      <LazyComponent {...props} />
    </LazyPageWrapper>
  ));
}